import {Component, OnDestroy, OnInit} from "@angular/core";
import {AuthService, LocalConfigurationService} from "@fedelta-pos/data-client";
import {AnalyticsService, LandingPage} from "../../orchestration/analytics.service";
import {Local} from "../../config-support/config";
import {IdentityModel, LoginStatus} from "../../identity/identity.model";
import {SessionModel} from "../../orchestration/session.model";
import {Subscription} from "rxjs";
import {SplashScreen} from "@ionic-native/splash-screen";
import {NavigationService} from "../../orchestration/navigation.service";
import {LoadingStatus} from "../../common-types/loading-status";
import {ButtonConnotation, ButtonProminence} from "../../buttons/button/button";
import {IdentificationMode} from "../../identity/identification-page/identification-mode";
import {IconButtonType} from "../../buttons/icon-button/icon-button";
import {ResponsiveService} from "../../orchestration/responsive.service";
import {Page} from "../../orchestration/page";
import {NavigationParams} from "../../orchestration/navigation-params";
import {ButtonStyle} from "../../orchestration/app-entity.model";

@Component({
    selector: "woa-entry-page",
    templateUrl: "./entry-page.component.html",
    providers: [{provide: Page, useExisting: EntryPageComponent}]
})
export class EntryPageComponent extends Page implements OnInit, OnDestroy {
    ButtonProminence = ButtonProminence;
    ButtonConnotation = ButtonConnotation;
    ButtonStyle = ButtonStyle;
    IdentificationMode = IdentificationMode;
    IconButtonType = IconButtonType;

    public urlDisplayed: boolean; // This is only enabled for demo purposes to allow the server to be manually specified.
    public serverURL: string; // Server URL to override in the local/dataserverURL config
    public demoMode: boolean = false; // Indicates if the app is set up in demo mode
    public Local: Local = Local;
    public appLoaded: boolean = undefined;
    public allowActions: boolean = false;
    private subscriptions: Subscription = new Subscription();
    private splashHidden: boolean = false;
    private destroyed: boolean = false;
    private readonly suppressReset: boolean;

    constructor(navigationParams: NavigationParams,
                public sessionModel: SessionModel,
                private analyticsService: AnalyticsService,
                private responsiveService: ResponsiveService,
                private authService: AuthService,
                private identityModel: IdentityModel,
                private navigationService: NavigationService,
                private splashScreen: SplashScreen,
                private localConfigurationService: LocalConfigurationService) {
        super();
        this.suppressReset = navigationParams.get("suppressReset");
        (async () => this.demoMode = await this.localConfigurationService.getBoolean(Local.DEMO))();
    }

    get showRetryButton(): boolean {
        return !this.urlDisplayed && this.appLoaded == false;
    }

    get showActionButtons(): boolean {
        return !this.urlDisplayed && this.appLoaded && this.allowActions;
    }

    get showStartAsGuestButton(): boolean {
        return this.showActionButtons && !this.identityModel.allowMemberLogin;
    }

    get showLoginButtons(): boolean {
        return this.showActionButtons && this.identityModel.allowMemberLogin;
    }

    get showRegisterButton(): boolean {
        return this.showLoginButtons && this.identityModel.supportsRegistration;
    }

    ngOnInit(): void {
        // noinspection JSIgnoredPromiseFromCall
        this.initialise();

        this.subscriptions.add(this.authService.loginRevoked$.subscribe(
            () => {
                if (this.appLoaded) {
                    // noinspection JSIgnoredPromiseFromCall
                    this.startAsGuest();
                }
            },
            (error: any) => {
                console.log(error);
                // noinspection JSIgnoredPromiseFromCall
                this.startAsGuest();
            }));
    }

    ngOnDestroy(): void {
        this.destroyed = true;
        this.hideNativeSplashScreen();
        this.subscriptions.unsubscribe();
    }

    /**
     * Retry after failed initial login
     */
    retryInitialLogin(): void {
        // noinspection JSIgnoredPromiseFromCall
        this.initialise();
    }

    async identify(mode: IdentificationMode): Promise<void> {
        if (await this.navigationService.createPage("woa-identification-page", {"mode": mode})) {
            // noinspection ES6MissingAwait
            this.sessionModel.goToLandingPage();
        }
    }

    async startAsGuest(): Promise<void> {
        if ((await this.identityModel.loginAsGuest()).status === LoginStatus.SUCCESS) {
            // noinspection ES6MissingAwait
            this.sessionModel.goToLandingPage(this.suppressReset);
        }
    }

    backgroundImageApplied(): void {
        if (!this.destroyed) {
            this.hideNativeSplashScreen();
        }
    }

    hideNativeSplashScreen(): void {
        if (this.responsiveService.isApp && !this.splashHidden) {
            this.splashScreen.hide();
            this.splashHidden = true;
        }
    }

    /**
     * Displays panel to set the server URL. Only when in demo mode
     */
    public async openChangeURL(): Promise<void> {
        if (!this.urlDisplayed) {
            this.serverURL = await this.localConfigurationService.getString(Local.DATASERVER_URL);
            this.urlDisplayed = true;
        }
    }

    /**
     * Closes the URL panel
     */
    public cancelURL(): void {
        this.urlDisplayed = false;
    }

    /**
     * Overrides the local/dataserverURL config
     */
    public saveURL(): void {
        this.localConfigurationService.saveString(Local.DATASERVER_URL, this.serverURL);
        this.urlDisplayed = false;
        // noinspection JSIgnoredPromiseFromCall
        this.initialise();
    }

    private async initialise(): Promise<void> {
        this.sessionModel.clear();
        this.appLoaded = undefined;
        this.allowActions = false;
        try {
            await this.initialLogin();
            await this.sessionModel.loadApp();
            this.appLoaded = true;

            // noinspection ES6MissingAwait
            this.sessionModel.notifyAppUsage(true);

            // If the login page is not required,
            // or if we are already logged in as a member, and member logins are (still) allowed,
            // proceed directly to the relevant landing page.
            if (!this.sessionModel.loginOptions.showCarousel || (this.identityModel.allowMemberLogin && !this.identityModel.isGuestUser)) {
                this.hideNativeSplashScreen();
                await this.sessionModel.goToLandingPage(this.suppressReset);
                this.allowActions = true;
            } else {
                // noinspection ES6MissingAwait
                this.analyticsService.trackLandingPage(LandingPage.LOGIN);
                this.allowActions = true;
                this.sessionModel.notifyInitialLoadStatus(LoadingStatus.LOADED);
            }
        } catch (error) {
            console.log("Failed to load app config", error);
            this.appLoaded = false;
            this.hideNativeSplashScreen();
            this.sessionModel.notifyInitialLoadStatus(LoadingStatus.FAILED);
        }
    }

    private async initialLogin(): Promise<any> {
        if (!this.authService.isLoggedIn()) {
            return this.identityModel.loginAsGuest();
        }
        try {
            if (!await this.authService.refreshAccessToken().toPromise()) {
                return this.identityModel.loginAsGuest();
            }
        } catch (error) {
            return this.identityModel.loginAsGuest();
        }
    }
}



// WEBPACK FOOTER //
// ./src/app/start/entry-page/entry-page.component.ts